import Ionicons from '@expo/vector-icons/Ionicons'
import {
  type GooglePlaceDetail,
  GooglePlacesAutocomplete,
} from 'react-native-google-places-autocomplete'
import {
  type AutoCompleteSearchMode,
  emptyAddress,
  type Address,
} from '@/src/common/components/AutoCompleteAddress/AutoCompleteAddressCommon'
import { useLogError } from '@/src/common/hooks/useLogError'
import { triggerSegmentAction } from '@/src/services/segment'
import { StyleSheet } from 'react-native'
import { getMesaApiRequestHeaders } from '@/src/common/utils/getMesaApiRequestHeaders'
import { type SardineId, sardineIdKey } from '@/src/services/Sardine/SardineId'
import { useSardine } from '@/src/services/Sardine'
import { fontFamilies, mesaColorNames } from '@/src/common/theme'
import { inputGap, inputMinHeight } from '@/src/components/ui/forms/inputs/Input'
import { GooglePlacesAutocompleteDefaultProps } from './GooglePlacesAutocompleteProps'

interface AutoCompleteAddressProps {
  onChange: (address: Address | undefined) => void
  onAddressUnlisted: () => void
  mode: AutoCompleteSearchMode
  testIDPrefix?: string
  sardineId?: SardineId
}

export function AutoCompleteInput(props: AutoCompleteAddressProps) {
  const logError = useLogError()

  const placeholder = props.mode === 'zip-code' ? '90210' : '123 Main St, City, State'

  const onAddressSelection = (data: GooglePlaceDetail | null) => {
    if (data === null) {
      props.onChange(undefined)
      return
    }

    void triggerSegmentAction({
      component: 'Input',
      action: 'User Picks a result for Google Places Autocomplete',
    })
    const newAddress = emptyAddress()

    data.address_components.forEach((component) => {
      if (component.types.includes('street_number') && component.long_name !== '') {
        newAddress.street1 += component.long_name + ' '
      }
      if (component.types.includes('route') && component.long_name !== '') {
        newAddress.street1 += component.long_name
      }
      if (component.types.includes('locality') && component.long_name !== '') {
        newAddress.city = component.long_name
      }
      if (component.types.includes('administrative_area_level_1') && component.short_name !== '') {
        newAddress.state = component.short_name
      }

      if (component.types.includes('postal_code') && component.short_name !== '') {
        newAddress.zipCode = component.short_name
      }
    })

    props.onChange(newAddress)
  }

  const { trackFocusChange, trackTextChange } = useSardine()

  return (
    <GooglePlacesAutocomplete
      {...GooglePlacesAutocompleteDefaultProps} // 👈️ Spread all default props
      requestUrl={{
        useOnPlatform: 'all',
        url: `${process.env.EXPO_PUBLIC_API_BASE_URL}/google-places`,
        headers: getMesaApiRequestHeaders(),
      }}
      renderLeftButton={() => (
        <Ionicons
          size={24}
          style={{ marginLeft: 12, alignSelf: 'center' }}
          name="location-outline"
          color={mesaColorNames['semantic/fg/subtle #898D8F']}
        />
      )}
      placeholder={placeholder}
      fetchDetails
      query={{
        language: 'en',
        components: 'country:us',
        type: props.mode === 'zip-code' ? '(regions)' : 'address',
      }}
      onPress={(_data, detail) => onAddressSelection(detail)}
      textInputProps={{
        placeholderTextColor: '#9da2a2',
        onChangeText: (text) => {
          if (text === '') {
            props.onChange(undefined)
          }
          props.sardineId && void trackTextChange(props.sardineId, text)
        },
        onFocus: () => {
          void triggerSegmentAction({
            component: 'Input',
            action: 'User Inputs Address for Google Places Autocomplete',
          })
          props.sardineId && void trackFocusChange(props.sardineId, true)
        },
        onBlur: () => {
          props.sardineId && void trackFocusChange(props.sardineId, false)
        },
        testID: props.testIDPrefix ? `${props.testIDPrefix}#AutoCompleteText` : undefined,
        ...(props.sardineId ? { dataSet: { [sardineIdKey]: props.sardineId } } : undefined),
      }}
      onFail={logError}
      styles={autocompleteStyles}
      disableScroll
    />
  )
}

const autocompleteStyles = StyleSheet.create({
  textInputContainer: {
    minHeight: inputMinHeight,
    fontFamily: fontFamilies['PlusJakartaSans-Regular'],
    fontWeight: 400,
    fontSize: 16,
    color: mesaColorNames['Greyscale/600 #505D64'],
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: mesaColorNames['Greyscale/300 #CDD4D5'],
    borderRadius: 8,
    overflow: 'hidden',
    alignItems: 'center',
  },
  textInput: {
    minHeight: inputMinHeight - 2,
    paddingHorizontal: inputGap,
    fontFamily: fontFamilies['PlusJakartaSans-Regular'],
    fontWeight: 400,
    fontSize: 16,
    color: mesaColorNames['Greyscale/600 #505D64'],
    marginBottom: 0,
    outlineWidth: 0,
  },
  description: {
    fontFamily: fontFamilies['PlusJakartaSans-Regular'],
    fontWeight: 400,
    fontSize: 14,
    color: mesaColorNames['Greyscale/600 #505D64'],
  },
  container: { paddingBottom: 10 },
} as const)
